import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { taskService } from '../services/api';
import { useAuth } from '../context/AuthContext';

const MyTasks = () => {
  const { currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState('created');
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    const fetchTasks = async () => {
      try {
        setLoading(true);
        // En una implementación real, se enviaría el filtro al backend
        const filter = activeTab === 'created' 
          ? { createdBy: currentUser.id }
          : { acceptedBy: currentUser.id };
          
        const response = await taskService.getTasks(filter);
        setTasks(response.data.tasks || []);
        setError(null);
      } catch (err) {
        console.error('Error al cargar tareas:', err);
        setError('Error al cargar las tareas. Por favor, intenta de nuevo.');
      } finally {
        setLoading(false);
      }
    };
    
    if (currentUser) {
      fetchTasks();
    }
  }, [activeTab, currentUser]);
  
  // Obtener color basado en el estado de la tarea
  const getStatusColor = (status) => {
    switch (status) {
      case 'pendiente': return 'warning';
      case 'aceptada': return 'info';
      case 'culminada': return 'secondary';
      case 'aprobada': return 'success';
      default: return 'primary';
    }
  };
  
  // Formatear fecha
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  
  if (loading) {
    return (
      <div className="text-center my-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Cargando...</span>
        </div>
        <p className="mt-2">Cargando tus tareas...</p>
      </div>
    );
  }
  
  return (
    <div className="my-tasks-container">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1>Mis Tareas</h1>
        <Link to="/tasks/create" className="btn btn-primary">
          <i className="fas fa-plus me-2"></i>
          Crear Nueva Tarea
        </Link>
      </div>
      
      <ul className="nav nav-tabs mb-4">
        <li className="nav-item">
          <button 
            className={`nav-link ${activeTab === 'created' ? 'active' : ''}`}
            onClick={() => setActiveTab('created')}
          >
            Tareas Creadas
          </button>
        </li>
        <li className="nav-item">
          <button 
            className={`nav-link ${activeTab === 'accepted' ? 'active' : ''}`}
            onClick={() => setActiveTab('accepted')}
          >
            Tareas Aceptadas
          </button>
        </li>
      </ul>
      
      {error ? (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      ) : tasks.length === 0 ? (
        <div className="alert alert-info" role="alert">
          {activeTab === 'created'
            ? 'No has creado ninguna tarea aún.'
            : 'No has aceptado ninguna tarea aún.'}
          {' '}
          {activeTab === 'created' ? (
            <Link to="/tasks/create">Crea tu primera tarea</Link>
          ) : (
            <Link to="/tasks">Explora las tareas disponibles</Link>
          )}
        </div>
      ) : (
        <div className="card">
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Título</th>
                    <th>Estado</th>
                    <th>{activeTab === 'created' ? 'Aceptada por' : 'Creada por'}</th>
                    <th>Tokens</th>
                    <th>Fecha</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {tasks.map(task => (
                    <tr key={task.id}>
                      <td><Link to={`/tasks/${task.id}`}>{task.title}</Link></td>
                      <td>
                        <span className={`badge bg-${getStatusColor(task.status)}`}>
                          {task.status === 'pendiente' ? 'Pendiente' : 
                           task.status === 'aceptada' ? 'En Progreso' : 
                           task.status === 'culminada' ? 'Completada' : 
                           'Aprobada'}
                        </span>
                      </td>
                      <td>
                        {activeTab === 'created' ? 
                          (task.acceptedBy ? task.acceptedBy.name : '—') :
                          task.createdBy.name}
                      </td>
                      <td>{task.tokens} BLUE</td>
                      <td>{formatDate(task.createdAt)}</td>
                      <td>
                        <Link to={`/tasks/${task.id}`} className="btn btn-sm btn-outline-primary">
                          <i className="fas fa-eye"></i>
                        </Link>
                        {/* Acciones adicionales según el estado */}
                        {activeTab === 'accepted' && task.status === 'aceptada' && (
                          <Link to={`/tasks/${task.id}`} className="btn btn-sm btn-outline-success ms-1">
                            <i className="fas fa-check"></i>
                          </Link>
                        )}
                        {activeTab === 'created' && task.status === 'culminada' && (
                          <Link to={`/tasks/${task.id}`} className="btn btn-sm btn-outline-success ms-1">
                            <i className="fas fa-dollar-sign"></i>
                          </Link>
                        )}
                        {task.acceptedBy && (
                          <Link to={`/chat/${activeTab === 'created' ? task.acceptedBy._id : task.createdBy._id}`} className="btn btn-sm btn-outline-info ms-1">
                            <i className="fas fa-comments"></i>
                          </Link>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyTasks;
